import { Fragment, useEffect, useRef, useState } from "react";
import {
  formatNumberWithCommas,
  isMobile,
  resetIsContinuousLoading,
  scrollToTop,
} from "@app/helpers";
import { setIsLoading, setShowAlert, store } from "@app/store";
import {
  getUser,
  apiGroupIsAbnormality,
  groupsMachinesReportsCSVGroup,
  groupsMachinesReportsList,
  groupsMachinesReportsGrid,
} from "@app/api";
import { useParams, Link } from "react-router-dom";
import {
  Button,
  FormControl,
  Menu,
  MenuItem,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  Table,
  TableHead,
  Paper,
  TableSortLabel,
  styled,
  TooltipProps,
  tooltipClasses,
  Switch,
  Skeleton,
} from "@mui/material";
import {
  ArrowBackIosNew,
  ArrowForwardIos,
  ViewModule,
  ViewList,
  CampaignOutlined,
} from "@mui/icons-material";
// import Add from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { format, addMonths, subMonths, addDays, subDays } from "date-fns";
import {
  FUEL_MAINTENANCE_REPORTS,
  INCIDENT_REPORTS,
  INSPECTION,
  INVALID_URL,
  LIMIT_PAGE,
  MAINTENANCE_REPORTS,
  MaintenanceReportType,
  ReportType,
  ReportTypeDialog,
  ResponseStatus,
} from "@app/constants";
import { DataDialog, DataPagination, DataTable } from "@app/components/atoms";
import {
  IconBox,
  IconInvalidUrl,
  IconLocationMachine,
  IconUser,
} from "@app/components/icons";
import { useStoreSelector } from "@app/hooks";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  FuelMaintenanceReportIcon,
  IncidentReportIcon,
  InspectionIconBlueSquare,
  InspectionIconMobile,
  MaintenanceReportIcon,
} from "@app/components/icons/ReportIcons/ReportIcons";
import { AllReportsDataDialog } from "./AllReportsDataDialog";
import {
  MachineSelectionDialog,
  type GroupMachineListItem,
} from "./MachineSelectionDialog";
import { CreateInspections } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports";
import { CreateAbnormality } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Abnormality/CreateAbnormality";
import { CreateRefuel } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Refuel/CreateRefuel";
import { CreateMaintenance } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Maintenance/CreateMaintenance";
import { DatePickerExcelInput } from "@app/components/atoms/DatePickerInputExcel/DatePickerInputExcel";
import { InspectionReportItemsDialog } from "./InspectionReportItemsDialog";
import {
  SummaryFilterType,
  GroupMachinesReports,
  MachineReport,
  MachineReportList,
  MachineReportGroup,
  MachineSummaryType,
} from "@app/types";
import { startOfMonth, endOfMonth, isThisMonth } from "date-fns";
import { DatePickerMachineReportHist } from "@app/components/atoms/DatePickerMachineReportHist/DatePickerMachineReportHist";
import "@app/pages/Features/GroupList/GroupDetail/MachineReportsHistoryTab/MachineReportsHistoryTab.css";
import { InputText } from "primereact/inputtext";
import React from "react";

const dataCountSummaryFilter: SummaryFilterType[] = [
  {
    label: "all",
    filterCondition: "",
    count: 0,
    key: "reportCount",
  },
  {
    label: "labelInspection",
    filterCondition: INSPECTION,
    count: 0,
    key: "inspectionReportCount",
    icon: <InspectionIconMobile />,
  },
  {
    label: "abnormalityOrIssue",
    filterCondition: INCIDENT_REPORTS,
    count: 0,
    key: "incidentReportCount",
    icon: <IncidentReportIcon />,
  },
  {
    label: "refuel",
    filterCondition: FUEL_MAINTENANCE_REPORTS,
    count: 0,
    key: "fuelMaintenanceReportCount",
    icon: <FuelMaintenanceReportIcon />,
  },
  {
    label: "maintenanceReport",
    filterCondition: MAINTENANCE_REPORTS,
    count: 0,
    key: "maintenanceReportCount",
    icon: <MaintenanceReportIcon />,
  },
];
const STORAGE_KEYS = {
  layoutViewMode: "machineReportsLayoutViewMode",
  dateViewMode: "machineReportsDateViewMode",
  currentDate: "machineReportsCurrentDate",
  filterCondition: "machineReportsFilterCondition",
  showAll: "machineReportsShowAll",
  page: "machineReportsPage",
  searchTextMachineReport: "machineReportsSearchTextMachine",
};

export function MachineReportsHistoryTab() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const stickyMachineColumnEdge = isRTL ? { right: 0 } : { left: 0 };
  const stickyMachineColumnMinWidth = 250;
  const stickyMachineColumnTextAlign = isRTL
    ? ("right" as const)
    : ("left" as const);
  const stickyMachineColumnCellSx = {
    textAlign: stickyMachineColumnTextAlign,
    direction: (isRTL ? "rtl" : "ltr") as "rtl" | "ltr",
  };
  const stickyMachineColumnFlexSx = {
    width: "100%",
    flexDirection: (isRTL ? "row-reverse" : "row") as "row-reverse" | "row",
    justifyContent: (isRTL ? "flex-end" : "flex-start") as
      | "flex-end"
      | "flex-start",
    textAlign: stickyMachineColumnTextAlign,
  };
  const stickyMachineColumnHeaderContentSx = {
    width: "100%",
    display: "flex",
    justifyContent: (isRTL ? "flex-end" : "flex-start") as
      | "flex-end"
      | "flex-start",
  };
  const stickyMachineColumnSortLabelSx = isRTL
    ? {
        display: "inline-flex",
        flexDirection: "row-reverse" as const,
        width: "auto",
        flexShrink: 0,
        "& .MuiTableSortLabel-icon": {
          margin: 0,
        },
      }
    : {
        display: "inline-flex",
        width: "auto",
        flexShrink: 0,
      };
  const stickyMachineColumnHeaderCellSx = stickyMachineColumnCellSx;
  const stickyMachineColumnFooterContentSx = {
    width: "100%",
    display: "flex",
    justifyContent: (isRTL ? "flex-end" : "flex-start") as
      | "flex-end"
      | "flex-start",
    textAlign: stickyMachineColumnTextAlign,
  };
  const isUpdatedMachineReportResponses = useStoreSelector(
    (state) => state.inspection.isUpdatedMachineReportResponses,
  );
  const [isInit, setIsInit] = useState(true);

  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const storedDate = localStorage.getItem(STORAGE_KEYS.currentDate);
    return storedDate ? new Date(storedDate) : new Date();
  });

  const [layoutViewMode, setLayoutViewMode] = useState<"grid" | "list">(
    () =>
      (localStorage.getItem(STORAGE_KEYS.layoutViewMode) as "grid" | "list") ||
      "grid",
  );

  const [dateViewMode, setDateViewMode] = useState<"month" | "day" | "all">(
    () =>
      (localStorage.getItem(STORAGE_KEYS.dateViewMode) as
        | "month"
        | "day"
        | "all") || "month",
  );
  const [dataSource, setDataSource] = useState<GroupMachinesReports | null>(
    null,
  );
  const [dataCell, setDataCell] = useState<MachineReport>();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [field, setField] = useState(
    layoutViewMode === "list" ? "reportedAt" : "customerMachineName",
  );

  const [order, setOrder] = useState<string | undefined>(
    layoutViewMode === "list" ? "DESC" : "ASC",
  );

  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem(STORAGE_KEYS.page);
    return savedPage ? Number(savedPage) : 1;
  });

  const [limit] = useState(LIMIT_PAGE);
  const [dataCountSummaryTabList, setDataCountSummaryTabList] = useState(
    dataCountSummaryFilter,
  );

  const [filterCondition, setFilterCondition] = useState<ReportTypeDialog | "">(
    () =>
      (localStorage.getItem(
        STORAGE_KEYS.filterCondition,
      ) as ReportTypeDialog) || "",
  );

  useEffect(() => {
    handleSort("asc", "customerMachineName");
  }, []);

  const [timeFormatWithoutTime, setTimeFormatWithoutTime] =
    useState("yyyy/MM/dd");
  const [timeFormatWithTime, setTimeFormatWithTime] =
    useState("yyyy/MM/dd HH:mm");

  const monthFormat = timeFormatWithoutTime
    .replace(/(^|[/. -])d{1,2}([/. -]|$)/gi, "$1")
    .replace(/^[/. -]+|[/. -]+$/g, "");

  const isAllPeriodView = dateViewMode === "all";
  const displayDate = isAllPeriodView
    ? t("group.allPeriod")
    : dateViewMode === "month"
    ? format(currentDate, monthFormat)
    : format(currentDate, timeFormatWithoutTime);

  const [machineReportId, setMachineReportId] = useState<string>("");
  const [openFormReport, setOpenFormReport] = useState<string>(INSPECTION);
  const [clickedReportVersion, setClickedReportVersion] = useState<
    string | null
  >(null);
  const [dialogKey, setDialogKey] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [localeCode, setLocaleCode] = useState("");
  const [openExportReports, setExportReports] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [showAbnormalityBanner, setShowAbnormalityBanner] = useState(false);

  const [createReportMenuAnchor, setCreateReportMenuAnchor] =
    useState<null | HTMLElement>(null);
  const isCreateReportMenuOpen = Boolean(createReportMenuAnchor);
  const [pendingReportType, setPendingReportType] = useState<string | null>(
    null,
  );
  const [machineSelectionOpen, setMachineSelectionOpen] = useState(false);
  const [selectedMachineForReport, setSelectedMachineForReport] =
    useState<GroupMachineListItem | null>(null);
  const [openCreateInspection, setOpenCreateInspection] = useState(false);
  const [openCreateAbnormality, setOpenCreateAbnormality] = useState(false);
  const [openCreateRefuel, setOpenCreateRefuel] = useState(false);
  const [openCreateMaintenance, setOpenCreateMaintenance] = useState(false);

  const createReportMenuOptions = dataCountSummaryFilter.filter(
    (tab) => tab.filterCondition && tab.filterCondition !== INSPECTION,
  );

  // const createReportMenuOptions = dataCountSummaryFilter.filter(
  //   (tab) => tab.filterCondition,
  // );

  const handleOpenCreateReport = (reportType: string) => {
    if (reportType === INSPECTION) {
      setOpenCreateInspection(true);
    } else if (reportType === INCIDENT_REPORTS) {
      setOpenCreateAbnormality(true);
    } else if (reportType === FUEL_MAINTENANCE_REPORTS) {
      setOpenCreateRefuel(true);
    } else if (reportType === MAINTENANCE_REPORTS) {
      setOpenCreateMaintenance(true);
    }
  };

  const handleCreateReportMenuItemClick = (reportType: string) => {
    setCreateReportMenuAnchor(null);
    setPendingReportType(reportType);
    setSelectedMachineForReport(null);
    setMachineSelectionOpen(true);
  };

  const handleMachineSelectionConfirm = (machine: GroupMachineListItem) => {
    setSelectedMachineForReport(machine);
    setMachineSelectionOpen(false);
    if (pendingReportType) {
      handleOpenCreateReport(pendingReportType);
    }
  };

  const handleMachineSelectionClose = () => {
    setMachineSelectionOpen(false);
    setPendingReportType(null);
  };

  const closeCreateReportDialog = () => {
    setOpenCreateInspection(false);
    setOpenCreateAbnormality(false);
    setOpenCreateRefuel(false);
    setOpenCreateMaintenance(false);
    setSelectedMachineForReport(null);
    setPendingReportType(null);
  };

  const reportGroupId = selectedMachineForReport?.groupId || (id ?? "");
  const reportMachineId = selectedMachineForReport?.machineId ?? "";

  const [showAll, setShowAll] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.showAll);
    return stored === null ? true : stored === "true";
  });

  const [loading, setLoading] = useState(false);

  const handleShowAllChange = () => {
    setShowAll((prev) => !prev);
    setPage(1);
  };

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = useState("");

  const [appliedSearch, setAppliedSearch] = useState(searchText);

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(searchText);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchText.trim()) {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchText("");
    setAppliedSearch("");
    setPage(1);
  };

  useEffect(() => {
    if (searchText === "") {
      setAppliedSearch("");
      setPage(1);
    }
  }, [searchText]);

  useEffect(() => {
    if (dateViewMode === "month") {
      const start = startOfMonth(currentDate);

      let end: Date;
      if (isThisMonth(currentDate)) {
        end = new Date();
      } else {
        end = endOfMonth(currentDate);
      }

      setValueStartExcelDate(start);
      setValueEndExcelDate(end);
    } else if (dateViewMode === "day") {
      setValueStartExcelDate(currentDate);
      setValueEndExcelDate(currentDate);
    }
  }, [dateViewMode, currentDate]);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await getUser();
        const { data } = res.data;

        if (!data) {
          resetIsContinuousLoading();
          return;
        }

        const baseFormat = data.timeFormat || "dd/MM/yyyy";

        let withTimeFormat = baseFormat;
        if (!baseFormat.includes("HH") || !baseFormat.includes("mm")) {
          withTimeFormat += " HH:mm";
        }
        setLocaleCode(data.isoLocaleCode);
        setTimeFormatWithoutTime(baseFormat);
        setTimeFormatWithTime(withTimeFormat);
      } catch (error) {
        console.error("Failed to load Me:", error);
      } finally {
        resetIsContinuousLoading();
        store.dispatch(setIsLoading(false));
        setLoading(false);
      }
    };

    fetchMe();
  }, [id]);

  const arrowButtonStyle = {
    width: 32,
    height: 32,
    color: "grey.600",
    border: "1px solid transparent",
    "&:hover": {
      borderColor: "#253fc8",
      backgroundColor: "transparent",
    },
  };

  const toggleButtonSelectedStyle = {
    "&.Mui-selected": {
      backgroundColor: "#eae9ff",
      borderColor: "#253fc8",
      color: "#253fc8",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#dedeff",
    },
  };

  const exportButtonStyle = {
    whiteSpace: "nowrap",
    width: { xs: "100%", sm: "auto" },
    alignSelf: { xs: "stretch", sm: "center" },
  };

  const formControlStyle = {
    minWidth: 80,
  };

  const containerStyle = {
    pt: 2,
    px: { xs: 2, sm: 0 },
  };

  const topRowStyle = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const leftPanelStyle = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    gap: { xs: 2, sm: 1 },
    flexWrap: "wrap",
  };

  const reportsTypesTabs = {
    mt: 2,
    mb: 0,
    ml: -1,
    px: { xs: 0, sm: 0 },
    overflowX: "auto",
    whiteSpace: "nowrap",
  };

  const resetAllStates = () => {
    setMachineReportId("");
    setOpenFormReport(INSPECTION);
    setClickedReportVersion(null);
    setPage(1);

    setDataCell(undefined);
    setDialogKey(0);
    setOpen(false);
    setDataSource(null);
  };

  const resetAllStatesFilter = () => {
    setMachineReportId("");
    setOpenFormReport(INSPECTION);
    setClickedReportVersion(null);
    setPage(1);
    setDataCell(undefined);
    setDialogKey(0);
    setOpen(false);
    setDataSource(null);
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.layoutViewMode, layoutViewMode);
  }, [layoutViewMode]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.dateViewMode, dateViewMode);
  }, [dateViewMode]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.currentDate, currentDate.toISOString());
  }, [currentDate]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.filterCondition, filterCondition);
  }, [filterCondition]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.showAll, String(showAll));
  }, [showAll]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.page, String(page));
  }, [page]);

  const handleSelectAllPeriodView = () => {
    if (dateViewMode === "all") return;

    setLayoutViewMode("list");
    setOpenCalendar(false);
    setDateViewMode("all");
    resetAllStates();
  };

  const handleViewAbnormalityReports = () => {
    setLayoutViewMode("list");
    setOpenCalendar(false);

    if (dateViewMode !== "all") {
      setDateViewMode("all");
    }

    setFilterCondition(ReportTypeDialog.INCIDENT_REPORTS);
    handleSort("DESC", "reportResponseStatus");
    resetAllStates();
  };

  const handleViewModeChange = (event: any) => {
    const newMode = event.target.value as "month" | "day" | "all";

    if (newMode !== dateViewMode) {
      if (newMode === "all") {
        handleSelectAllPeriodView();
        return;
      }
      setDateViewMode(newMode);

      resetAllStates();
    }
  };

  const fullyResetGridListSwitch = (newViewMode: "grid" | "list") => {
    setPage(1);
    localStorage.setItem(STORAGE_KEYS.page, "1");

    if (newViewMode === "list") {
      setField("reportedAt");
      setOrder("DESC");
    } else {
      setField("customerMachineName");
      setOrder("ASC");
    }

    setFilterCondition("");
    localStorage.setItem(STORAGE_KEYS.filterCondition, "");

    setSearchText("");
    setAppliedSearch("");
    localStorage.setItem(STORAGE_KEYS.searchTextMachineReport, "");

    resetAllStates();
  };

  const handleLayoutViewChange = (
    e: React.MouseEvent<HTMLElement>,
    newValue: "grid" | "list" | null,
  ) => {
    if (dateViewMode === "all" && newValue === "grid") return;
    if (!newValue || newValue === layoutViewMode) return;

    setLayoutViewMode(newValue);

    fullyResetGridListSwitch(newValue);
  };

  const columnsListDayMonth = [
    {
      key: "machineIconWithName",
      title: t("machineInfo.customerMachineNumber"),
      dataIndex: "customerMachineName",
      sorted: true,
      tableHeadingColspan: 1,
      className: "pl-0 txt-pre-wrap",
      style: {
        width: 350,
        minWidth: 350,
        maxWidth: 350,
        whiteSpace: "nowrap",
      },

      render: (_: any, record: MachineReportList) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginLeft: 8,
            paddingRight: 0,
            borderRight: "1px solid #D8D8D8",
            height: "100%",
          }}
        >
          {record.pictureUrl ? (
            <Avatar
              variant="square"
              src={record.pictureUrl}
              sx={{
                width: 60,
                height: 60,
                borderRadius: "4px",
                backgroundColor: "#f5f5f5",
              }}
              imgProps={{
                style: {
                  objectFit: "contain",
                  width: "100%",
                  height: "100%",
                },
                onError: () => {
                  const updatedMachines = (
                    (dataSource?.data
                      ?.machinesReports as MachineReportList[]) ?? []
                  ).map((item) =>
                    item.machineId === record.machineId
                      ? { ...item, pictureUrl: null }
                      : item,
                  );
                  if (dataSource) {
                    setDataSource({
                      ...dataSource,
                      data: {
                        ...dataSource.data,
                        machinesReports: updatedMachines as MachineReportList[],
                      },
                    });
                  }
                },
              }}
            />
          ) : (
            <Box
              sx={{
                width: 60,
                height: 60,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "4px",
                backgroundColor: "#f0f0f0",
                flex: "0 0 auto",
              }}
            >
              <IconInvalidUrl />
            </Box>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: 1.4,
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <Link
              to={`/groups/${id}/machines/${record.machineId}/report`}
              title={record.customerMachineName}
              style={{
                paddingLeft: 0,
                textDecoration: "underline",
                color: "#140a9a",
                fontWeight: 500,

                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",

                wordBreak: "break-word",
                overflowWrap: "anywhere",

                whiteSpace: "normal",

                minWidth: 100,
                maxWidth: 350,
              }}
            >
              {record.customerMachineName}
            </Link>
          </div>
        </div>
      ),
    },
    {
      key: "reportedAt",
      title: "tableMachineDetail.date",
      dataIndex: "reportedAt",
      align: "left",
      sorted: true,
      style: {
        minWidth: 0,
        fontSize: 14,
        whiteSpace: "nowrap",
      },
      render: (value: any) => {
        return format(new Date(value), timeFormatWithTime);
      },
      isLabel: true,
    },
    {
      key: "reportType",
      title: "subType",
      dataIndex: "reportType",
      align: "left",
      sorted: true,
      forceRender: true,
      style: {
        minWidth: 0,
        whiteSpace: "nowrap",
      },
      render: (_: string, record: any) => (
        <div className="flex align-items-center justify-content-start">
          <div
            className="item-name ml-5 fs-14 word-break txt-pre-wrap"
            style={{
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {record?.reportType === "INSPECTION" && <InspectionIconMobile />}
            {record?.reportType === "INCIDENT_REPORTS" && (
              <IncidentReportIcon />
            )}
            {record?.reportType === "FUEL_MAINTENANCE_REPORTS" && (
              <FuelMaintenanceReportIcon />
            )}
            {record?.reportType === "MAINTENANCE_REPORTS" && (
              <MaintenanceReportIcon />
            )}
          </div>
        </div>
      ),
      isLabel: true,
    },

    {
      key: "reportItem",
      title: "tableMachineDetail.item",
      dataIndex: "reportItem",
      align: "left",
      sorted: true,
      style: {
        minWidth: isMobile() ? 0 : 0,
        fontSize: 14,
        whiteSpace: "nowrap",
      },
      render: (value: string, record: any) => {
        if (!value) return null;

        const parts = value.split("®");

        return (
          <span
            className="txt-link"
            onClick={(e) => handleClick(e, record)}
            title={value}
            style={{
              display: "inline-block",
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "bottom",
              textDecorationSkipInk: "auto",
            }}
          >
            {parts.map((part, index) => (
              <React.Fragment key={index}>
                {part}
                {index < parts.length - 1 && (
                  <span
                    style={{
                      fontSize: "0.65em",
                      position: "relative",
                      top: "-0.5em",
                      marginLeft: "1px",
                      textDecoration: "none",
                    }}
                  >
                    ®
                  </span>
                )}
              </React.Fragment>
            ))}
          </span>
        );
      },
      className: "txt-pre-wrap",
    },

    {
      key: "reportResponseStatus",
      title: "dialogReport.status",
      dataIndex: "reportResponseStatus",
      align: "left",
      sorted: true,
      style: {
        paddingLeft: 0,
        paddingRight: 0,
        minWidth: 0,
        whiteSpace: "nowrap",
      },
      render: (value: ResponseStatus) =>
        !!value && (
          <span
            className={`label-status fs-14 ${
              value === ResponseStatus.RESOLVED
                ? "label-status-active"
                : "label-status-disable"
            } flex align-items-center justify-content-start`}
          >
            <span className="status" />
            <span className="label">
              {value === ResponseStatus.RESOLVED ? t("resolved") : t("open")}
            </span>
          </span>
        ),
      isLabel: true,
    },
    {
      key: "serviceMeter",
      title: "tableMachineDetail.serviceMeter",
      dataIndex: "serviceMeter",
      align: "center",
      style: {
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0,
        fontSize: 14,
        whiteSpace: "nowrap",
      },
      tableHeadingClassName: "txt-pre",
      forceRender: true,
      isLabel: true,
      sorted: true,

      render: (value: number | string) => {
        if (value === null || value === undefined || value === "") return "";
        return formatNumberWithCommas(value);
      },
    },
    {
      key: "userPictureUrl",
      title: "",
      dataIndex: "userPictureUrl",
      align: "center",
      sorted: true,
      forceRender: true,
      render: (_: string, record: any) => (
        <div className="flex-centered justify-content-start">
          {!!record?.userPictureUrl ? (
            record?.userPictureUrl === INVALID_URL ? (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconInvalidUrl />
              </Box>
            ) : (
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                }}
                alt="avatar-reporter"
                src={record?.userPictureUrl}
                imgProps={{
                  onError: () => {
                    const newReports = (
                      (dataSource?.data
                        ?.machinesReports as MachineReportGroup[]) ?? []
                    ).map((machine: MachineReportGroup) => ({
                      ...machine,
                      reports: machine.reports.map((r) => {
                        if (r.reportedId === record.reportedId) {
                          return {
                            ...r,
                            userPictureUrl: INVALID_URL,
                          };
                        }
                        return r;
                      }),
                    }));

                    if (dataSource) {
                      setDataSource({
                        ...dataSource,
                        data: {
                          ...dataSource.data,
                          machinesReports: newReports,
                        },
                      });
                    }
                  },
                }}
              />
            )
          ) : (
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconUser />
            </Box>
          )}
        </div>
      ),
      className: "pr-4",
      tableHeadingClassName: "d-none-imp",
      style: {
        minWidth: 0,
        whiteSpace: "nowrap",
      },
    },
    {
      key: "reporterName",
      title: "tableMachineDetail.reporter",
      dataIndex: "reporterName",
      align: "left",
      sorted: true,
      style: {
        minWidth: 0,
        fontSize: 14,
        whiteSpace: "nowrap",
      },
      tableHeadingColspan: 2,
      className: "pl-0 txt-pre-wrap",
    },
    {
      key: "lat",
      title: "tableMachineDetail.location",
      dataIndex: "lat",
      style: {
        minWidth: 0,
        whiteSpace: "nowrap",
      },
      align: "center",
      render: (_: string, record: any) =>
        record?.lat &&
        record?.lng && (
          <span
            className="flex-centered pointer"
            onClick={() => handleClickLocation(record)}
          >
            <IconLocationMachine />
          </span>
        ),
      isLabel: true,
    },
  ];

  const showSummaryRow =
    layoutViewMode === "grid" &&
    dateViewMode === "day" &&
    (dataSource?.data?.machinesReports?.length ?? 0) > 0;

  const summaryRow: MachineReportGroup = {
    groupName: "",
    machineId: "summary-row",
    customerMachineName: t("tableMachineDetail.allMachinesTotal"),
    lastServiceMeter: 0,
    lastServiceMeterUpdated: "",
    serialNumber: "",
    modelType: "",
    pictureUrl: null,
    machineManufacturerName: "",
    allTotalFuelAmount: dataSource?.data?.machineTotalFuelAmount ?? 0,
    reports: [],
  };

  const machinesReportsWithSummary = showSummaryRow
    ? [...(dataSource?.data?.machinesReports ?? []), summaryRow]
    : dataSource?.data?.machinesReports ?? [];

  const columnsGridDay = [
    {
      key: "machineIconWithName",
      title: t("machineInfo.customerMachineNumber"),
      dataIndex: "customerMachineName",
      sorted: true,
      tableHeadingColspan: 1,
      className: "pl-0 txt-pre-wrap",

      style: {
        width: 350,
        minWidth: 350,
        maxWidth: 350,
        whiteSpace: "nowrap",
      },

      render: (_: any, record: MachineReportGroup) => {
        if (record.machineId === "summary-row") {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                marginLeft: 2,
                paddingRight: 0,
                borderRight: "1px solid #D8D8D8",
                paddingTop: 0,
                fontWeight: 600,
                color: "#000",
              }}
            >
              {t("tableMachineDetail.allMachinesTotal")}
            </Box>
          );
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginLeft: 8,
              paddingRight: 0,
              borderRight: "1px solid #D8D8D8",
              height: "100%",
              minWidth: 0,
            }}
          >
            {record.pictureUrl ? (
              <Avatar
                variant="square"
                src={record.pictureUrl}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "4px",
                  backgroundColor: "#f5f5f5",
                }}
                imgProps={{
                  style: {
                    objectFit: "contain",
                    width: "100%",
                    height: "100%",
                  },
                  onError: () => {
                    const updatedMachines = (
                      (dataSource?.data
                        ?.machinesReports as MachineReportGroup[]) ?? []
                    ).map((item: MachineReportGroup) =>
                      item.machineId === record.machineId
                        ? { ...item, pictureUrl: null }
                        : item,
                    );

                    if (dataSource) {
                      setDataSource({
                        ...dataSource,
                        data: {
                          ...dataSource.data,
                          machinesReports: updatedMachines,
                        },
                      });
                    }
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "4px",
                  backgroundColor: "#f0f0f0",
                  flex: "0 0 auto",
                }}
              >
                <IconInvalidUrl />
              </Box>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 1.4,
                minWidth: 0,
                flex: "1 1 auto",
              }}
            >
              <Link
                to={`/groups/${id}/machines/${record.machineId}/report`}
                title={record.customerMachineName}
                style={{
                  paddingLeft: 0,
                  textDecoration: "underline",
                  color: "#140a9a",
                  fontWeight: 500,

                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",

                  minWidth: 100,
                  maxWidth: 350,
                }}
              >
                {record.customerMachineName}
              </Link>
            </div>
          </div>
        );
      },
    },

    {
      key: "reports",
      title: t("tableMachineDetail.report"),
      dataIndex: "reports",
      render: (_: any, record: MachineReportGroup) => {
        if (record.machineId === "summary-row") {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                marginLeft: 0,
                paddingRight: 0,
                borderRight: "1px solid #D8D8D8",
                paddingTop: 0,
                fontWeight: 600,
                color: "#000",
              }}
            >
              <span>&nbsp;</span>
            </Box>
          );
        }
        if (!record.reports || record.reports.length === 0) {
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                color: "#888",
              }}
            >
              –
            </Box>
          );
        }

        const isAllNullReport = (report: any) => {
          return (
            report.reportedId === null &&
            report.reportedAt === null &&
            (report.reportItem === null ||
              report.reportItem === "" ||
              report.reportItem === "null") &&
            (report.reportType === null || report.reportType === "null")
          );
        };

        let validReports = record.reports.filter((r) => !isAllNullReport(r));
        validReports = validReports.sort((a, b) => {
          const t1 = a.reportedAt ? new Date(a.reportedAt).getTime() : 0;
          const t2 = b.reportedAt ? new Date(b.reportedAt).getTime() : 0;
          return t1 - t2;
        });
        if (validReports.length === 0) {
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                color: "#888",
              }}
            >
              –
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              whiteSpace: "nowrap",
              gap: 1,
              paddingRight: 0,
              borderRight: "1px solid #D8D8D8",
              height: "100%",
            }}
          >
            {validReports.map((report, index) => {
              const getIcon = () => {
                switch (report.reportType) {
                  case "INSPECTION":
                    return <InspectionIconMobile />;
                  case "INCIDENT_REPORTS":
                    return <IncidentReportIcon />;
                  case "FUEL_MAINTENANCE_REPORTS":
                    return <FuelMaintenanceReportIcon />;
                  case "MAINTENANCE_REPORTS":
                    return <MaintenanceReportIcon />;
                  default:
                    return null;
                }
              };

              return (
                <Fragment key={report.reportedId}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      backgroundColor: "#F8FAFC",

                      borderRadius: "8px",
                      padding: "6px 10px",
                      fontSize: 12,
                      minWidth: 0,

                      cursor: "pointer",
                    }}
                    title={report.reportItem ?? ""}
                    onClick={(e) => {
                      handleClick(e, report);
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {getIcon()}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        ml: 0.5,
                        gap: 0,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: "bold" }}>
                        {format(new Date(report.reportedAt), "HH:mm")}
                      </span>
                      <span
                        style={{
                          fontWeight: "bold",

                          textDecoration: "underline",
                          color: "#140a9a",
                          marginTop: 0,
                          display: "inline-block",
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          verticalAlign: "bottom",
                        }}
                      >
                        {report.reportItem}
                      </span>
                    </Box>
                  </Box>

                  {index < validReports.length - 1 && (
                    <Box
                      sx={{
                        width: 16,
                        height: 4,
                        backgroundColor: "#bbb",
                        borderRadius: 2,
                        mx: 1,
                        alignSelf: "center",
                      }}
                    />
                  )}
                </Fragment>
              );
            })}
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                whiteSpace: "nowrap",
                gap: 1,
                paddingRight: 0,
                borderRight: "1px solid #D8D8D8",
                height: "100%",
              }}
            ></Box>
          </Box>
        );
      },
    },
    {
      key: "lastServiceMeter",
      title: t("tableMachineDetail.lastServiceMeter"),
      dataIndex: "lastServiceMeter",
      align: "center",
      style: {
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0,
        fontSize: 14,
        whiteSpace: "nowrap",
      },
      tableHeadingClassName: "txt-pre",
      forceRender: true,
      isLabel: true,
      sorted: true,
      render: (value: number | string, record: MachineReportGroup) => {
        if (record.machineId === "summary-row") {
          return <span style={{ fontWeight: 600, color: "#000" }}></span>;
        }

        if (value === null || value === undefined || value === "") return "";
        return formatNumberWithCommas(value);
      },
    },

    {
      key: "allTotalFuelAmount",
      title: t("tableMachineDetail.totalFuelAmount"),
      dataIndex: "allTotalFuelAmount",
      align: "center",
      style: {
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0,
        fontSize: 14,
        whiteSpace: "nowrap",
      },
      tableHeadingClassName: "txt-pre",
      forceRender: true,
      isLabel: true,
      sorted: true,
      render: (value: number | string, record: MachineReportGroup) => {
        if (record.machineId === "summary-row") {
          return (
            <strong style={{ color: "#000" }}>
              {formatNumberWithCommas(value)}
            </strong>
          );
        }

        if (value === null || value === undefined || value === "") return "";
        return formatNumberWithCommas(value);
      },
    },
  ];

  const handleClickLocation = (column: any) => {
    const convertColumn = {
      machineName: column?.reportTitle,
      ...column,
    };
    setDataCell(convertColumn);
    setOpen(true);
  };

  const initScrollToTop = () => {
    if (isInit) {
      scrollToTop();
      setIsInit(false);
    }
  };

  const handleSort = (orderBy: string | undefined, property: string) => {
    setField(property);
    setOrder(orderBy);
  };

  const handleClick = (_: React.MouseEvent<HTMLElement>, target: any) => {
    setMachineReportId(target.reportedId);
    setOpenFormReport(target.reportType);
    setClickedReportVersion(target.reportVersion ?? null);
    setDialogKey((prev) => prev + 1);
  };

  const handleArrowBack = () => {
    if (dateViewMode === "all") return;
    setCurrentDate((prev) => {
      const newDate =
        dateViewMode === "day" ? subDays(prev, 1) : subMonths(prev, 1);

      if (newDate.getTime() !== prev.getTime()) {
        resetAllStates();
      }

      return newDate;
    });
  };

  const handleArrowForward = () => {
    if (dateViewMode === "all") return;
    const today = new Date();

    if (dateViewMode === "day") {
      const nextDay = addDays(currentDate, 1);
      if (nextDay <= today) {
        setCurrentDate(nextDay);
        resetAllStates();
      }
    } else {
      const nextMonth = addMonths(startOfMonth(currentDate), 1);
      if (nextMonth <= endOfMonth(today)) {
        setCurrentDate(nextMonth);
        resetAllStates();
      }
    }
  };

  const handleClickfilterCondition = (
    _: React.MouseEvent<HTMLElement>,
    newFilter: ReportTypeDialog | "",
  ) => {
    if (newFilter !== filterCondition) {
      setFilterCondition(newFilter);
      resetAllStatesFilter();
    }
  };

  const [valueStartExcelDate, setValueStartExcelDate] = useState(() => {
    const date = new Date();
    return date;
  });

  const [valueEndExcelDate, setValueEndExcelDate] = useState(() => {
    const date = new Date();
    return date;
  });

  const isExportDisabled =
    !valueStartExcelDate ||
    !valueEndExcelDate ||
    valueEndExcelDate < valueStartExcelDate;

  const handleExportReportsClick = async () => {
    if (!id) {
      console.error("group ID is missing.");
      return;
    }

    const formattedStart = format(valueStartExcelDate, "yyyy-MM-dd");
    const formattedEnd = format(valueEndExcelDate, "yyyy-MM-dd");

    try {
      const response = await groupsMachinesReportsCSVGroup(
        id,
        formattedStart,
        formattedEnd,
        timeZone,
      );

      const data = response?.data?.data;

      if (!data || !Array.isArray(data) || data.length === 0) {
        store.dispatch(setShowAlert(t("exportreportfailedNoData")));
        return;
      }

      const headers = [
        t("groupName"),
        t("machineInfo.maker"),
        t("machineInfo.customerMachineNumber"),
        t("modelType"),
        t("machineInfo.serialNo"),
        t("tableMachineDetail.date"),
        t("subType"),
        t("tableMachineDetail.item"),
        t("dialogReport.comment"),
        t("fuelAmount"),
        t("adBlueAmount"),
        t("serviceMeterOrSMR"),
        t("tableMachineDetail.reporter"),
        t("latitude"),
        t("longitude"),
      ];

      const rows = data.map((row) => [
        row.groupName ?? "",
        row.machineManufacturerName ?? "",
        row.customerMachineName ?? "",
        row.modelType ?? "",
        row.serialNumber ?? "",
        format(new Date(row.dateTime), timeFormatWithTime) ?? "",
        row.reportTypeMessage ?? "",
        row.item ?? "",
        row.comment ?? "",
        row.fuelAmount ?? "",
        row.adblueAmount ?? "",
        row.serviceMeter ?? "",
        row.reporterName ?? "",
        row.lat ?? "",
        row.lng ?? "",
      ]);

      const csvContent = [headers, ...rows]
        .map((e) =>
          e.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      store.dispatch(setShowAlert(t("exportreportssuccessfully")));
      const dateStart = format(
        valueStartExcelDate,
        timeFormatWithoutTime,
      ).replace(/[-/]/g, "");
      const dateEnd = format(valueEndExcelDate, timeFormatWithoutTime).replace(
        /[-/]/g,
        "",
      );
      link.download = `${data[0].groupName}_${dateStart}-${dateEnd}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportReports(false);
    } catch (error) {
      console.error("Export failed:", error);
      store.dispatch(setShowAlert(t("exportreportfailed")));
    }
  };

  const handleChangeStartExcelDate = (newValue: Date) => {
    setValueStartExcelDate(newValue);
  };

  const handleChangeEndExcelDate = (newValue: Date) => {
    if (newValue && newValue < valueStartExcelDate) return;
    setValueEndExcelDate(newValue);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "INCIDENT_REPORTS":
        return <IncidentReportIcon />;
      case "FUEL_MAINTENANCE_REPORTS":
        return <FuelMaintenanceReportIcon />;
      case "MAINTENANCE_REPORTS":
        return <MaintenanceReportIcon />;
      default:
        return null;
    }
  };

  const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "white",
      maxHeight: 280,
      overflowY: "auto",
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: "white",
    },
  }));

  const ReportPopupContent = ({
    reports,
    onClick,
  }: {
    reports: MachineReport[];
    onClick: (report: MachineReport) => void;
  }) => {
    const getIcon = (type: string) => {
      switch (type) {
        case "INSPECTION":
          return <InspectionIconMobile />;
        case "INCIDENT_REPORTS":
          return <IncidentReportIcon />;
        case "FUEL_MAINTENANCE_REPORTS":
          return <FuelMaintenanceReportIcon />;
        case "MAINTENANCE_REPORTS":
          return <MaintenanceReportIcon />;
        default:
          return null;
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          p: 0.2,
          bgcolor: "white",
        }}
      >
        {reports
          .slice()
          .sort(
            (a, b) =>
              new Date(a.reportedAt).getTime() -
              new Date(b.reportedAt).getTime(),
          )
          .map((report) => (
            <Box
              key={report.reportedId}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "#f5f6f9",
                borderRadius: "8px",
                p: "15px",
                fontSize: 12,
                minWidth: 0,
                cursor: "pointer",
                flexWrap: "nowrap",
                overflow: "hidden",
              }}
              onClick={() => onClick(report)}
            >
              <Box
                sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}
              >
                {getIcon(report.reportType)}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  minWidth: 0,
                  flex: "1 1 auto",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: "bold",
                    alignSelf: "flex-start",
                    color: "#000",
                    lineHeight: 1.2,
                  }}
                >
                  {format(new Date(report.reportedAt), "HH:mm")}
                </span>

                {report.reportItem && (
                  <Tooltip
                    arrow
                    title={
                      <span>
                        {report.reportItem.split("®").map((part, index) => (
                          <React.Fragment key={index}>
                            {part}
                            {index <
                              report.reportItem.split("®").length - 1 && (
                              <span
                                style={{
                                  fontSize: "0.65em",
                                  position: "relative",
                                  top: "-0.45em",
                                  marginLeft: "1px",
                                }}
                              >
                                ®
                              </span>
                            )}
                          </React.Fragment>
                        ))}
                      </span>
                    }
                  >
                    <span
                      style={{
                        fontWeight: "bold",
                        color: "#000",
                        marginTop: -2,
                        display: "block",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {report.reportItem.split("®").map((part, index) => (
                        <React.Fragment key={index}>
                          {part}
                          {index < report.reportItem.split("®").length - 1 && (
                            <span
                              style={{
                                fontSize: "0.65em",
                                position: "relative",
                                top: "-0.45em",
                                marginLeft: "1px",
                              }}
                            >
                              ®
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
      </Box>
    );
  };

  useEffect(() => {
    if (layoutViewMode === "list") {
      handleSort("desc", "reportedAt");
    }
  }, [layoutViewMode]);

  useEffect(() => {
    if (!id) return;

    const fetchReports = async () => {
      try {
        setLoading(true);
        const month =
          dateViewMode === "month" ? format(currentDate, "yyyy-MM") : "";
        const date =
          dateViewMode === "day" ? format(currentDate, "yyyy-MM-dd") : "";
        const isAllDateRange = dateViewMode === "all";

        const reportType = filterCondition || "ALL";

        const apiFn =
          layoutViewMode === "list"
            ? groupsMachinesReportsList
            : groupsMachinesReportsGrid;

        const res = await apiFn(
          id,
          layoutViewMode,
          month,
          date,
          timeZone,
          reportType,
          page.toString(),
          limit.toString(),
          order,
          field,
          showAll,
          appliedSearch,
          layoutViewMode === "list" ? isAllDateRange : undefined,
        );
        setDataSource(res.data);
        const summary = res.data.data as MachineSummaryType;
        const updatedTabs = dataCountSummaryFilter.map((tab) => ({
          ...tab,
          count: summary[tab.key as keyof MachineSummaryType] || 0,
        }));
        setDataCountSummaryTabList(updatedTabs);
      } catch (error) {
        console.error("Failed to load machine reports:", error);
      } finally {
        store.dispatch(setIsLoading(false));
        setLoading(false);
      }
    };

    fetchReports();
  }, [
    id,
    currentDate,
    filterCondition,
    page,
    limit,
    order,
    field,
    isUpdatedMachineReportResponses,
    layoutViewMode,
    dateViewMode,
    showAll,
    appliedSearch,
    timeZone,
  ]);

  useEffect(() => {
    if (!id) return;

    const fetchGroupAbnormality = async () => {
      try {
        const res = await apiGroupIsAbnormality(id);
        setShowAbnormalityBanner(!!res?.data?.data?.isAbnormality);
      } catch (error) {
        console.error("Failed to load group abnormality status:", error);
      }
    };

    fetchGroupAbnormality();
  }, [id, isUpdatedMachineReportResponses]);

  return (
    <Box sx={containerStyle}>
      {showAbnormalityBanner && (
        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#fff",
            borderInlineStart: "5px solid #DA9309",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <CampaignOutlined sx={{ color: "#DA9309", fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("group.unaddressedAbnormalityNotice")}
            </Typography>
          </Box>

          <Button
            variant="text"
            sx={{
              minWidth: "auto",
              textTransform: "none",
              fontWeight: 600,
              color: "#253fc8",
              p: 0,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
            onClick={handleViewAbnormalityReports}
          >
            {t("group.viewReports")} &gt;
          </Button>
        </Box>
      )}

      <Box sx={topRowStyle}>
        <Box sx={leftPanelStyle}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              ref={anchorRef}
              variant="h6"
              fontWeight="bold"
              sx={{
                width: 100,
                textAlign: "left",
                whiteSpace: "nowrap",
                cursor: isAllPeriodView ? "default" : "pointer",
              }}
              onClick={() => {
                if (!isAllPeriodView) {
                  setOpenCalendar(true);
                }
              }}
            >
              {displayDate}
            </Typography>

            {dateViewMode === "month" ? (
              <DatePickerMachineReportHist
                label=""
                value={currentDate}
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    setCurrentDate(newValue);
                    resetAllStates();
                  }
                  setOpenCalendar(false);
                }}
                onAccept={(newValue: Date | null) => {
                  if (newValue) {
                    setCurrentDate(newValue);
                    resetAllStates();
                  }
                  setOpenCalendar(false);
                }}
                maxDate={new Date()}
                views={["year", "month"]}
                open={openCalendar}
                onClose={() => setOpenCalendar(false)}
                slotProps={{
                  popper: {
                    anchorEl: anchorRef.current,
                    placement: "bottom-start",
                  },
                }}
                openTo="month"
              />
            ) : dateViewMode === "day" ? (
              <DatePickerMachineReportHist
                label=""
                value={currentDate}
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    setCurrentDate(newValue);
                    resetAllStates();
                  }
                  setOpenCalendar(false);
                }}
                onAccept={(newValue: Date | null) => {
                  if (newValue) setCurrentDate(newValue);
                  setOpenCalendar(false);
                }}
                maxDate={new Date()}
                open={openCalendar}
                onClose={() => setOpenCalendar(false)}
                slotProps={{
                  popper: {
                    anchorEl: anchorRef.current,
                    placement: "bottom-start",
                  },
                }}
              />
            ) : null}

            <IconButton
              sx={arrowButtonStyle}
              onClick={handleArrowBack}
              disabled={isAllPeriodView}
            >
              <ArrowBackIosNew
                sx={{ fontSize: 14 }}
                style={{
                  transform:
                    document.documentElement.dir === "rtl"
                      ? "rotate(180deg)"
                      : "none",
                }}
              />
            </IconButton>

            <IconButton
              sx={arrowButtonStyle}
              onClick={handleArrowForward}
              disabled={
                dateViewMode === "month"
                  ? addMonths(startOfMonth(currentDate), 1) >
                    endOfMonth(new Date())
                  : addDays(currentDate, 1) > new Date()
              }
            >
              <ArrowForwardIos
                sx={{ fontSize: 14 }}
                style={{
                  transform:
                    document.documentElement.dir === "rtl"
                      ? "rotate(180deg)"
                      : "none",
                }}
              />
            </IconButton>
          </Box>

          <FormControl size="small" sx={formControlStyle}>
            <Select value={dateViewMode} onChange={handleViewModeChange}>
              <MenuItem value="all">{t("all")}</MenuItem>
              <MenuItem value="day">{t("day")}</MenuItem>
              <MenuItem value="month">{t("month")}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ direction: "ltr", display: "inline-flex" }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={layoutViewMode}
              onChange={handleLayoutViewChange}
            >
              <Tooltip
                title={
                  isAllPeriodView ? t("group.panelViewLimited") : t("panelView")
                }
                arrow
              >
                <span>
                  <ToggleButton
                    value="grid"
                    sx={toggleButtonSelectedStyle}
                    disabled={isAllPeriodView}
                  >
                    <ViewModule fontSize="small" />
                  </ToggleButton>
                </span>
              </Tooltip>

              <Tooltip title={t("listView")} arrow>
                <ToggleButton value="list" sx={toggleButtonSelectedStyle}>
                  <ViewList fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>

          {layoutViewMode === "grid" && filterCondition === "" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">{t("showAll")}</Typography>
              <Switch
                checked={showAll}
                className={`btn-switch ${showAll ? "checked" : ""}`}
                onChange={handleShowAllChange}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {/* <Button
            className="btn btn-primary"
            sx={{
              whiteSpace: { xs: "normal", sm: "nowrap" },
              flex: { xs: "1 1 auto", sm: "0 1 auto" },
              minWidth: { sm: "min-content" },
              width: { xs: "100%", sm: "auto" },
              maxWidth: "100%",
              py: { xs: 1, sm: undefined },
              "& .MuiButton-startIcon": {
                marginInlineEnd: "0px",
              },
            }}
            startIcon={<Add sx={{ fontSize: "1.35rem" }} />}
            onClick={(event) => setCreateReportMenuAnchor(event.currentTarget)}
          >
            {t("report.createReports")}
          </Button> */}
          <Menu
            anchorEl={createReportMenuAnchor}
            open={isCreateReportMenuOpen}
            onClose={() => setCreateReportMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {createReportMenuOptions.map((option) => (
              <MenuItem
                key={option.filterCondition}
                onClick={() =>
                  handleCreateReportMenuItemClick(option.filterCondition)
                }
                sx={{ gap: "8px", minWidth: 200 }}
              >
                {option.icon && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {option.icon}
                  </Box>
                )}
                {t(option.label)}
              </MenuItem>
            ))}
          </Menu>

          <Button
            variant="outlined"
            className="btn btn-no-border"
            sx={exportButtonStyle}
            onClick={() => {
              setExportReports(true);
            }}
          >
            {t("excel")}
          </Button>
        </Box>
      </Box>

      <Box sx={reportsTypesTabs}>
        <div className="toggle-btn-group sp-p24">
          <ToggleButtonGroup
            sx={{
              overflowY: "hidden !important",
            }}
            exclusive
            value={filterCondition}
          >
            {dataCountSummaryTabList.map((tab, index) => {
              const isInspection = tab.filterCondition === INSPECTION;

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginInline: 0.5,
                  }}
                >
                  <ToggleButton
                    value={tab.filterCondition}
                    className="flex-centered filter-tab"
                    onClick={handleClickfilterCondition}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {tab.icon && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {tab.icon}
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        ...(tab.icon && { marginInlineStart: 1 }),
                      }}
                    >
                      {t(tab.label)}
                      <span style={{ marginInlineStart: 8 }}>
                        ({tab.count})
                      </span>
                    </Box>
                  </ToggleButton>
                  {isInspection &&
                    layoutViewMode !== "list" &&
                    dateViewMode !== "day" && (
                      <Box
                        sx={{
                          mt: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          pointerEvents: "none",
                          userSelect: "none",
                          fontSize: 12,
                        }}
                      >
                        (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <InspectionIconBlueSquare />
                          </Box>
                          <span style={{ marginInlineStart: 4 }}>
                            {" "}
                            {t("labelInspection")}
                          </span>
                        </Box>
                        )
                      </Box>
                    )}
                </Box>
              );
            })}
          </ToggleButtonGroup>
        </div>
      </Box>

      {layoutViewMode === "list" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 0,
            pr: 0,
            marginTop: 2,
          }}
        >
          <div style={{ position: "relative" }}>
            <InputText
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("Customermachinenumber")}
              className="custom-input"
              style={{
                width: "277px",
                paddingLeft: "1rem",
                paddingRight: "2rem",
                height: "50px",
                border: "1.5px solid #bcbcbc",
                borderRadius: "30px",
                boxSizing: "border-box",
              }}
            />
            {searchText && (
              <button
                className="clear-button"
                onClick={clearSearch}
                aria-label="Clear search"
                type="button"
              >
                ×
              </button>
            )}
          </div>
        </Box>
      )}

      {layoutViewMode === "list" && (
        <>
          <Box sx={{ marginTop: 2 }}>
            <div className="container">
              <div className="row">
                <DataTable
                  columns={columnsListDayMonth}
                  dataSource={
                    dataSource
                      ? (
                          dataSource.data.machinesReports as MachineReportList[]
                        ).map((machine) => {
                          return {
                            id: `${machine.machineId}-${machine.reportedId}`,
                            ...machine,
                            machineId: machine.machineId,
                            groupName: machine.groupName,
                            customerMachineName: machine.customerMachineName,
                            lastServiceMeter: machine.lastServiceMeter,
                            lastServiceMeterUpdated:
                              machine.lastServiceMeterUpdated,
                            serialNumber: machine.serialNumber,
                            modelType: machine.modelType,
                            pictureUrl: machine.pictureUrl,
                            machineManufacturerName:
                              machine.machineManufacturerName,
                          };
                        })
                      : []
                  }
                  onRequestSort={handleSort}
                  didScrollTop={() => {
                    initScrollToTop();
                    resetIsContinuousLoading();
                  }}
                  textEmptyData="noReport"
                  loading={!dataSource}
                />
                <DataPagination
                  isHidden={!dataSource?.data?.machinesReports?.length}
                  currentPage={page}
                  limitPage={limit}
                  totalPage={dataSource?.meta?.pageInfo?.allTotal}
                  handleChangePage={(newPage) => {
                    setPage(newPage);

                    setMachineReportId("");
                    setOpenFormReport(INSPECTION);
                    setClickedReportVersion(null);
                  }}
                  className="mt-25 sp-p24"
                />
              </div>
            </div>
          </Box>
        </>
      )}

      {layoutViewMode === "grid" && dateViewMode === "day" && (
        <>
          <Box sx={{ marginTop: 2 }}>
            <div className="container">
              <div className="row">
                <DataTable
                  columns={columnsGridDay}
                  dataSource={machinesReportsWithSummary}
                  onRequestSort={handleSort}
                  didScrollTop={() => {
                    initScrollToTop();
                    resetIsContinuousLoading();
                  }}
                  textEmptyData="noReport"
                  loading={loading}
                />
                <DataPagination
                  isHidden={!dataSource?.data?.machinesReports?.length}
                  currentPage={page}
                  limitPage={limit}
                  totalPage={dataSource?.meta?.pageInfo?.total}
                  handleChangePage={(newPage) => {
                    setPage(newPage);

                    setMachineReportId("");
                    setOpenFormReport(INSPECTION);
                    setClickedReportVersion(null);
                  }}
                  className="mt-25 sp-p24"
                />
              </div>
            </div>
          </Box>
        </>
      )}

      {dataCell && (
        <DataDialog
          disableBackdropClick={true}
          title={t("tableMachineDetail.location")}
          open={open}
          setOpen={setOpen}
          className="dialog-location-report"
        >
          <iframe
            title="map-location"
            className="map-iframe"
            loading="lazy"
            src={`https://maps.google.com/maps?&q="+${dataCell?.lat}, ${dataCell?.lng}"&output=embed`}
          />
          <span className="fs-14 txt-gray-1 label-dialog">
            {t("tableMachineDetail.date")}
          </span>
          <p className="fs-16 txt-gray mt-4">
            {dataCell?.firstReportedAt &&
              format(new Date(dataCell?.firstReportedAt), timeFormatWithTime)}
          </p>
          <span className="fs-14 txt-gray-1 label-dialog">
            {t("tableMachineDetail.reporter")}
          </span>
          <div className="flex-centered justify-content-start mt-8">
            {dataCell?.userPictureUrl ? (
              dataCell.userPictureUrl === INVALID_URL ? (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconInvalidUrl />
                </Box>
              ) : (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                  }}
                  alt="avatar-reporter"
                  src={dataCell?.userPictureUrl}
                  imgProps={{
                    onError: () => {
                      setDataCell({
                        ...dataCell,
                        userPictureUrl: INVALID_URL,
                      });
                    },
                  }}
                />
              )
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconUser />
              </Box>
            )}

            <span className="fs-16 txt-gray ml-5 word-break txt-pre-wrap">
              {localeCode === "ja"
                ? `${dataCell?.surname} ${dataCell?.givenName}`
                : `${dataCell?.givenName} ${dataCell?.surname}`}
            </span>
          </div>
          <div className="flex justify-content-end">
            <Button
              onClick={() => setOpen(false)}
              className="btn btn-no-border"
            >
              {t("dialogRefuel.close")}
            </Button>
          </div>
        </DataDialog>
      )}

      <DataDialog
        disableBackdropClick={true}
        open={openExportReports}
        setOpen={setExportReports}
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{t("exportreports")}</span>
            <span
              className="fs-16 txt-gray-3"
              style={{ color: "#6B7280", marginTop: "-4px" }}
            ></span>
          </div>
        }
      >
        <div
          className="date-range-responsive"
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: "16px",
            marginTop: "20px",
            flexWrap: "nowrap",
          }}
        >
          <div className="datepicker-wrapper">
            <DatePickerExcelInput
              label={t("report.labelStartDate")}
              value={valueStartExcelDate}
              onChange={handleChangeStartExcelDate}
              onAccept={handleChangeStartExcelDate}
            />
          </div>

          <div
            className="tilde-symbol"
            style={{
              fontSize: "20px",
              fontWeight: 600,
              alignSelf: "center",
              paddingBottom: "6px",
            }}
          >
            ~
          </div>

          <div className="datepicker-wrapper">
            <DatePickerExcelInput
              label={t("report.labelEndDate")}
              value={valueEndExcelDate}
              onChange={handleChangeEndExcelDate}
              onAccept={handleChangeEndExcelDate}
              minDate={valueStartExcelDate}
            />
          </div>
        </div>
        <div
          className="flex justify-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            style={{ width: "auto", marginTop: "4px" }}
            className="btn btn-no-border"
            onClick={() => {
              setExportReports(false);
            }}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn btn-primary"
            style={{ width: "auto", marginTop: "4px" }}
            disabled={isExportDisabled}
            onClick={handleExportReportsClick}
          >
            {t("export")}
          </Button>
        </div>
      </DataDialog>

      {machineReportId &&
        openFormReport !== ReportType.INSPECTION &&
        (() => {
          const matchedMachineForReport = (
            dataSource?.data?.machinesReports as Array<
              MachineReportGroup | MachineReportList
            >
          )?.find((m: MachineReportGroup | MachineReportList) =>
            "reports" in m
              ? m.reports.some(
                  (r: MachineReport) => r.reportedId === machineReportId,
                )
              : m.reportedId === machineReportId,
          );
          return (
            <AllReportsDataDialog
              key={dialogKey}
              id={id}
              machineId={matchedMachineForReport?.machineId || ""}
              machineModelType={matchedMachineForReport?.modelType || ""}
              machineReportId={machineReportId}
              reportType={openFormReport}
              reportVersion={clickedReportVersion}
              onClose={() => {
                setMachineReportId("");
                setOpenFormReport(INSPECTION);
                setClickedReportVersion(null);
              }}
            />
          );
        })()}

      {machineReportId && openFormReport === ReportType.INSPECTION && (
        <InspectionReportItemsDialog
          key={dialogKey}
          id={id}
          machineId={
            (
              dataSource?.data?.machinesReports as Array<
                MachineReportGroup | MachineReportList
              >
            )?.find((m: MachineReportGroup | MachineReportList) =>
              "reports" in m
                ? m.reports.some(
                    (r: MachineReport) => r.reportedId === machineReportId,
                  )
                : m.reportedId === machineReportId,
            )?.machineId || ""
          }
          machineReportId={machineReportId}
          onClose={() => {
            setMachineReportId("");
          }}
        />
      )}

      {layoutViewMode === "grid" && dateViewMode === "month" && (
        <Box sx={{ marginTop: 2 }}>
          {loading ? (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 400,
                overflowX: "auto",
                borderRadius: "8px",
              }}
            >
              <Table
                stickyHeader
                size="small"
                dir={isRTL ? "rtl" : "ltr"}
                sx={{ minWidth: 1000, tableLayout: "auto" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        ...stickyMachineColumnEdge,
                        zIndex: 3,
                        backgroundColor: "white",
                        minWidth: stickyMachineColumnMinWidth,
                        ...stickyMachineColumnCellSx,
                      }}
                    >
                      <Skeleton variant="text" width={120} height={24} />
                    </TableCell>

                    {Array.from({
                      length: new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() + 1,
                        0,
                      ).getDate(),
                    }).map((_, i) => (
                      <TableCell
                        key={`skeleton-head-${i}`}
                        align="center"
                        sx={{ minWidth: 50, border: "1px solid #ccc" }}
                      >
                        <Skeleton
                          variant="text"
                          width={20}
                          height={24}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>
                    ))}

                    <TableCell
                      align="center"
                      sx={{ minWidth: 160, border: "1px solid #ccc" }}
                    >
                      <Skeleton
                        variant="text"
                        width={100}
                        height={24}
                        sx={{ mx: "auto" }}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ minWidth: 180, border: "1px solid #ccc" }}
                    >
                      <Skeleton
                        variant="text"
                        width={120}
                        height={24}
                        sx={{ mx: "auto" }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Array.from({ length: 5 }).map((_, rowIndex) => (
                    <TableRow key={`skeleton-row-${rowIndex}`}>
                      <TableCell
                        sx={{
                          position: "sticky",
                          ...stickyMachineColumnEdge,
                          zIndex: 2,
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          minWidth: stickyMachineColumnMinWidth,
                          ...stickyMachineColumnCellSx,
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={stickyMachineColumnFlexSx}
                        >
                          <Skeleton
                            variant="rectangular"
                            width={40}
                            height={40}
                            sx={{ borderRadius: "4px", flexShrink: 0 }}
                          />
                          <Skeleton variant="text" width={100} height={20} />
                        </Box>
                      </TableCell>

                      {Array.from({
                        length: new Date(
                          currentDate.getFullYear(),
                          currentDate.getMonth() + 1,
                          0,
                        ).getDate(),
                      }).map((_, i) => (
                        <TableCell
                          key={`skeleton-cell-${rowIndex}-${i}`}
                          sx={{
                            border: "1px solid #ccc",
                            padding: 0,
                            textAlign: "center",
                          }}
                        >
                          {(rowIndex + i) % 5 === 0 && (
                            <Skeleton
                              variant="rectangular"
                              width={32}
                              height={32}
                              sx={{
                                borderRadius: "4px",
                                mx: "auto",
                                my: "4px",
                              }}
                            />
                          )}
                        </TableCell>
                      ))}

                      <TableCell
                        align="center"
                        sx={{ border: "1px solid #ccc" }}
                      >
                        <Skeleton
                          variant="text"
                          width={80}
                          height={20}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{ border: "1px solid #ccc" }}
                      >
                        <Skeleton
                          variant="text"
                          width={80}
                          height={20}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : dataSource?.data?.machinesReports?.length &&
            (dataSource?.data?.machinesReports as MachineReportGroup[])[0]
              ?.reports ? (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 400,
                  overflowX: "auto",
                  overflowY: "visible",
                  position: "relative",
                  borderRadius: "8px",
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  dir={isRTL ? "rtl" : "ltr"}
                  sx={{
                    borderCollapse: "collapse",
                    minWidth: 1000,
                    tableLayout: "auto",
                    borderRadius: "8px",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align={isRTL ? "right" : "left"}
                        sx={{
                          position: "sticky",
                          ...stickyMachineColumnEdge,
                          zIndex: 3,
                          backgroundColor: "white",
                          whiteSpace: "nowrap",
                          minWidth: stickyMachineColumnMinWidth,
                          borderTop: "1px solid #ccc",
                          borderBottom: "1px solid #ccc",
                          borderLeft: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                          fontWeight: "bold",
                          ...stickyMachineColumnHeaderCellSx,
                        }}
                        sortDirection={
                          field === "customerMachineName"
                            ? (order as "asc" | "desc")
                            : false
                        }
                      >
                        <Box sx={stickyMachineColumnHeaderContentSx}>
                          <TableSortLabel
                            sx={stickyMachineColumnSortLabelSx}
                            active={field === "customerMachineName"}
                            direction={order === "asc" ? "asc" : "desc"}
                            onClick={() => {
                              handleSort(
                                order === "asc" ? "desc" : "asc",
                                "customerMachineName",
                              );
                            }}
                          >
                            {t("machineInfo.customerMachineNumber")}
                          </TableSortLabel>
                        </Box>
                      </TableCell>

                      {Array.from({
                        length: new Date(
                          currentDate.getFullYear(),
                          currentDate.getMonth() + 1,
                          0,
                        ).getDate(),
                      }).map((_, i) => {
                        const headerDate = new Date(
                          currentDate.getFullYear(),
                          currentDate.getMonth(),
                          i + 1,
                        );
                        const today = new Date();
                        const isFuture = headerDate > today;

                        return (
                          <TableCell
                            key={`day-${i + 1}`}
                            align="center"
                            sx={{
                              minWidth: 50,
                              border: "1px solid #ccc",
                              fontWeight: "bold",
                              cursor: isFuture ? "not-allowed" : "pointer",
                              color: isFuture ? "#bbb" : "inherit",
                              backgroundColor:
                                !isFuture &&
                                headerDate.toDateString() ===
                                  currentDate.toDateString()
                                  ? "white"
                                  : "white",
                            }}
                            onClick={() => {
                              if (isFuture) return;
                              setDateViewMode("day");
                              setCurrentDate(headerDate);
                              resetAllStates();
                            }}
                          >
                            {i + 1}
                          </TableCell>
                        );
                      })}

                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 160,
                          border: "1px solid #ccc",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                        sortDirection={
                          field === "lastServiceMeter"
                            ? (order as "asc" | "desc")
                            : false
                        }
                      >
                        <TableSortLabel
                          active={field === "lastServiceMeter"}
                          direction={order === "asc" ? "asc" : "desc"}
                          onClick={() => {
                            handleSort(
                              order === "asc" ? "desc" : "asc",
                              "lastServiceMeter",
                            );
                          }}
                        >
                          {t("tableMachineDetail.lastServiceMeter")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 180,
                          border: "1px solid #ccc",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                        sortDirection={
                          field === "allTotalFuelAmount"
                            ? (order as "asc" | "desc")
                            : false
                        }
                      >
                        <TableSortLabel
                          active={field === "allTotalFuelAmount"}
                          direction={order === "asc" ? "asc" : "desc"}
                          onClick={() => {
                            handleSort(
                              order === "asc" ? "desc" : "asc",
                              "allTotalFuelAmount",
                            );
                          }}
                        >
                          {t("tableMachineDetail.totalFuelAmount")}
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dataSource?.data?.machinesReports ?? []).map((record) => (
                      <TableRow
                        key={record.machineId}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease-in-out",
                          },

                          "&:hover td": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        <TableCell
                          align={isRTL ? "right" : "left"}
                          sx={{
                            position: "sticky",
                            ...stickyMachineColumnEdge,
                            zIndex: 2,
                            backgroundColor: "white",
                            minWidth: stickyMachineColumnMinWidth,
                            borderTop: "1px solid #ccc",
                            borderBottom: "1px solid #ccc",
                            borderLeft: "1px solid #ccc",
                            borderRight: "1px solid #ccc",
                            ...stickyMachineColumnCellSx,
                            "&:hover": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ minWidth: 0, ...stickyMachineColumnFlexSx }}
                          >
                            {record.pictureUrl ? (
                              <Avatar
                                variant="square"
                                src={record.pictureUrl}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  flex: "0 0 auto",
                                  borderRadius: "4px",
                                  backgroundColor: "#f5f5f5",
                                }}
                                imgProps={{
                                  style: {
                                    objectFit: "contain",
                                    width: "100%",
                                    height: "100%",
                                  },
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderRadius: "4px",
                                  backgroundColor: "#f9f9f9",
                                  flex: "0 0 auto",
                                }}
                              >
                                <IconInvalidUrl />
                              </Box>
                            )}

                            <Box
                              display="flex"
                              flexDirection="column"
                              sx={{
                                minWidth: 0,
                                flex: "1 1 auto",
                                lineHeight: 1.4,
                                alignItems: isRTL ? "flex-end" : "flex-start",
                                textAlign: stickyMachineColumnTextAlign,
                              }}
                            >
                              <Link
                                to={`/groups/${id}/machines/${record.machineId}/report`}
                                title={record.customerMachineName}
                                style={{
                                  textDecoration: "underline",
                                  color: "#140a9a",
                                  fontWeight: "bold",

                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",

                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                  textAlign: stickyMachineColumnTextAlign,

                                  minWidth: 0,
                                  maxWidth: "100%",
                                }}
                              >
                                {record.customerMachineName}
                              </Link>
                            </Box>
                          </Box>
                        </TableCell>

                        {Array.from({
                          length: new Date(
                            currentDate.getFullYear(),
                            currentDate.getMonth() + 1,
                            0,
                          ).getDate(),
                        }).map((_, i) => {
                          const currentDay = new Date(
                            currentDate.getFullYear(),
                            currentDate.getMonth(),
                            i + 1,
                          );
                          const dayReports =
                            "reports" in record
                              ? record.reports.filter((report) => {
                                  const reportDate = new Date(
                                    report.reportedAt,
                                  );

                                  return (
                                    reportDate.getFullYear() ===
                                      currentDay.getFullYear() &&
                                    reportDate.getMonth() ===
                                      currentDay.getMonth() &&
                                    reportDate.getDate() ===
                                      currentDay.getDate()
                                  );
                                })
                              : [record].filter((report) => {
                                  const reportDate = new Date(
                                    report.reportedAt,
                                  );
                                  return (
                                    reportDate.getFullYear() ===
                                      currentDay.getFullYear() &&
                                    reportDate.getMonth() ===
                                      currentDay.getMonth() &&
                                    reportDate.getDate() ===
                                      currentDay.getDate()
                                  );
                                });

                          return (
                            <TableCell
                              key={`day-cell-${record.machineId}-${i + 1}`}
                              sx={{
                                whiteSpace: "nowrap",
                                border: "1px solid #ccc",
                                textAlign: "center",
                                verticalAlign: "middle",
                                position: "relative",
                                overflow: "visible",
                                padding: 0,
                              }}
                            >
                              {dayReports.length ===
                              0 ? null : dayReports.length === 1 ? (
                                <Box
                                  position="relative"
                                  padding="8px"
                                  sx={{
                                    cursor: "pointer",
                                  }}
                                >
                                  <LightTooltip
                                    arrow
                                    placement="bottom"
                                    sx={{ backgroundColor: "white" }}
                                    title={
                                      <ReportPopupContent
                                        reports={[dayReports[0]]}
                                        onClick={(report) => {
                                          handleClick(null as any, report);
                                        }}
                                      />
                                    }
                                  >
                                    <Box
                                      onClick={() =>
                                        handleClick(null as any, dayReports[0])
                                      }
                                      sx={{
                                        cursor: "pointer",
                                        width: 40,
                                        height: 40,
                                        borderRadius: "4px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",

                                        backgroundColor:
                                          dayReports[0].reportType ===
                                          "INSPECTION"
                                            ? "#cdd6ff"
                                            : "transparent",
                                      }}
                                    >
                                      {getIcon(dayReports[0].reportType)}
                                    </Box>
                                  </LightTooltip>
                                </Box>
                              ) : (
                                <Box position="relative" padding="8px">
                                  <LightTooltip
                                    arrow
                                    placement="bottom"
                                    sx={{ backgroundColor: "white" }}
                                    title={
                                      <ReportPopupContent
                                        reports={dayReports}
                                        onClick={(report) => {
                                          handleClick(null as any, report);
                                        }}
                                      />
                                    }
                                  >
                                    <Box
                                      sx={{
                                        borderRadius: "4px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        mx: "auto",
                                        width: 40,
                                        height: 40,
                                        backgroundColor: dayReports.some(
                                          (r) => r.reportType === "INSPECTION",
                                        )
                                          ? "#cdd6ff"
                                          : "transparent",
                                      }}
                                    >
                                      {(() => {
                                        const types = [
                                          ...new Set(
                                            dayReports.map((r) => r.reportType),
                                          ),
                                        ];

                                        if (
                                          types.includes("INSPECTION") &&
                                          types.length === 2 &&
                                          dayReports.length === 2
                                        ) {
                                          const otherType = types.find(
                                            (t) => t !== "INSPECTION",
                                          );
                                          return getIcon(otherType!);
                                        }

                                        return (
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              width: 25,
                                              height: 25,
                                              borderRadius: "50%",
                                              backgroundColor: "#6699ff",
                                              fontSize: 12,
                                              fontWeight: "bold",
                                              color: "white",
                                            }}
                                          >
                                            {dayReports.length}
                                          </Box>
                                        );
                                      })()}
                                    </Box>
                                  </LightTooltip>
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell
                          align="center"
                          sx={{
                            border: "1px solid #ccc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatNumberWithCommas(
                            record.lastServiceMeter ?? "",
                          )}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            border: "1px solid #ccc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatNumberWithCommas(
                            record.allTotalFuelAmount ?? "",
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease-in-out",
                        },

                        "&:hover td": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                    >
                      <TableCell
                        align={isRTL ? "right" : "left"}
                        sx={{
                          fontWeight: "bold",
                          border: "1px solid #ccc",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          ...stickyMachineColumnEdge,
                          zIndex: 2,
                          backgroundColor: "white",
                          minWidth: stickyMachineColumnMinWidth,
                          ...stickyMachineColumnCellSx,
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={stickyMachineColumnFooterContentSx}
                        >
                          {t("tableMachineDetail.allMachinesTotal")}
                        </Box>
                      </TableCell>

                      <TableCell
                        colSpan={new Date(
                          currentDate.getFullYear(),
                          currentDate.getMonth() + 1,
                          0,
                        ).getDate()}
                        sx={{
                          border: "1px solid #ccc",
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      />

                      <TableCell
                        sx={{
                          border: "1px solid #ccc",
                          fontWeight: "bold",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      ></TableCell>

                      <TableCell
                        sx={{
                          border: "1px solid #ccc",
                          fontWeight: "bold",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      >
                        {formatNumberWithCommas(
                          dataSource?.data?.machineTotalFuelAmount ?? "",
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <DataPagination
                isHidden={!dataSource?.data?.machinesReports?.length}
                currentPage={page}
                limitPage={limit}
                totalPage={dataSource?.meta?.pageInfo?.total}
                handleChangePage={(newPage) => {
                  setPage(newPage);

                  setMachineReportId("");
                  setOpenFormReport(INSPECTION);
                  setClickedReportVersion(null);
                }}
                className="mt-25 sp-p24"
              />
            </>
          ) : (
            <div className="empty-table bg-white">
              <div className="text-center">
                <IconBox />
                <p className="text-content"> {t("noReport")}</p>
              </div>
            </div>
          )}
        </Box>
      )}

      <MachineSelectionDialog
        open={machineSelectionOpen}
        groupId={id ?? ""}
        onClose={handleMachineSelectionClose}
        onConfirm={handleMachineSelectionConfirm}
      />

      <CreateInspections
        open={openCreateInspection}
        onClose={closeCreateReportDialog}
        groupId={reportGroupId}
        machineId={reportMachineId}
      />

      <CreateAbnormality
        open={openCreateAbnormality}
        onClose={closeCreateReportDialog}
        groupId={reportGroupId}
        machineId={reportMachineId}
      />

      <CreateRefuel
        open={openCreateRefuel}
        onClose={closeCreateReportDialog}
        groupId={reportGroupId}
        machineId={reportMachineId}
      />

      <CreateMaintenance
        open={openCreateMaintenance}
        onClose={closeCreateReportDialog}
        groupId={reportGroupId}
        machineId={reportMachineId}
        machineModelType={selectedMachineForReport?.modelAndType || ""}
        maintenanceType={MaintenanceReportType.PERIODIC_MAINTENANCE}
      />
    </Box>
  );
}
